import React, { useState, useEffect } from "react";
import { InputAdornment, Select, MenuItem } from "@material-ui/core";
import InputField from "../InputField/InputField";
import useStyles from "./PhoneNumberInput.style";

const PhoneNumberInput = ({
  id,
  name,
  value,
  placeholder = "Mobile number",
  handleChange = () => {},
  handleBlur = () => {},
  isError = false,
  helperText,
  disabled = false,
  isFromCheckout = false,
  isSignupFormm = false,
  style,
  initialPrefix = "+64",
  onPrefixChange = () => {},
  maxLength = null,
  ...props
}) => {
  const classes = useStyles();
  
  // Auto-detect prefix from existing phone number
  const detectPrefixFromValue = (phoneNumber) => {
    if (!phoneNumber) return initialPrefix;
    
    const phoneStr = phoneNumber.toString().trim();
    if (phoneStr.startsWith("+61")) return "+61";
    if (phoneStr.startsWith("+64")) return "+64";
    if (phoneStr.startsWith("61") && phoneStr.length > 9) return "+61";
    if (phoneStr.startsWith("64") && phoneStr.length > 9) return "+64";
    
    return initialPrefix;
  };
  
  const [prefixNo, setPrefixNo] = useState(detectPrefixFromValue(value) || initialPrefix);

  // Clean phone number to avoid duplicating prefix
  const cleanPhoneNumber = (phoneNumber, prefix) => {
    if (!phoneNumber) return "";
    
    // Convert to string and trim whitespace
    let cleaned = phoneNumber.toString().trim();
    
    // Remove any existing prefix to avoid duplication (order matters - longer prefixes first)
    if (cleaned.startsWith("+64")) {
      cleaned = cleaned.replace("+64", "");
    } else if (cleaned.startsWith("+61")) {
      cleaned = cleaned.replace("+61", "");
    } else if (cleaned.startsWith("64") && cleaned.length > 9) {
      // Only remove "64" if it's likely a country code (phone number is long enough)
      cleaned = cleaned.replace(/^64/, "");
    } else if (cleaned.startsWith("61") && cleaned.length > 9) {
      // Only remove "61" if it's likely a country code (phone number is long enough)
      cleaned = cleaned.replace(/^61/, "");
    }
    
    // Remove leading zeros
    while (cleaned.startsWith("0") && cleaned.length > 1) {
      cleaned = cleaned.substring(1);
    }
    
    // Remove any non-numeric characters
    cleaned = cleaned.replace(/[^0-9]/g, '');
    
    return cleaned;
  };

  const handlePhoneChange = (e) => {
    // Call preventDefault on the original event if it exists
    if (e.preventDefault && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    let cleanedNumber = cleanPhoneNumber(e.target.value, prefixNo);
    
    // Apply maxLength constraint if specified
    if (maxLength && cleanedNumber.length > maxLength) {
      cleanedNumber = cleanedNumber.substring(0, maxLength);
    }
    
    handleChange({
      ...e,
      preventDefault: () => {}, // Add preventDefault method to avoid errors
      stopPropagation: e.stopPropagation ? e.stopPropagation.bind(e) : () => {},
      target: {
        ...e.target,
        value: cleanedNumber,
        name: name,
        id: id
      }
    });
  };

  const handlePrefixChange = (e) => {
    const newPrefix = e.target.value;
    setPrefixNo(newPrefix);
    onPrefixChange(newPrefix);
  };

  useEffect(() => {
    // Update prefix when value changes (to handle existing phone numbers)
    const detectedPrefix = detectPrefixFromValue(value);
    if (detectedPrefix !== prefixNo && value) {
      setPrefixNo(detectedPrefix);
      onPrefixChange(detectedPrefix);
    }
  }, [value]);

  useEffect(() => {
    if (initialPrefix && initialPrefix !== prefixNo && !value) {
      setPrefixNo(initialPrefix);
    }
  }, [initialPrefix]);

  return (
    <InputField
      id={id}
      name={name}
      type="tel"
      placeholder={placeholder}
      value={cleanPhoneNumber(value, prefixNo)}
      handleChange={handlePhoneChange}
      handleBlur={handleBlur}
      isError={isError}
      helperText={helperText}
      disabled={disabled}
      isFromCheckout={isFromCheckout}
      isSignupFormm={isSignupFormm}
      style={style}
      startAdornment={
        <InputAdornment position="start">
          <Select
            value={prefixNo}
            onChange={handlePrefixChange}
            variant="standard"
            className={classes.prefixStyle}
            disabled={disabled}
          >
            <MenuItem value="+64">+64</MenuItem>
            <MenuItem value="+61">+61</MenuItem>
          </Select>
        </InputAdornment>
      }
      {...props}
    />
  );
};

export default PhoneNumberInput; 